"use client";

import { c as _c } from "react/compiler-runtime";
import parse from "html-react-parser";
import type { FC, PointerEventHandler, ReactNode } from "react";
import { useEffect, useEffectEvent, useId, useLayoutEffect, useRef, useState } from "react";
import { LuMinus, LuPlus, LuScan } from "react-icons/lu";
import { Button } from "@/components/button";
import type { ComponentPropsWithoutChildren } from "@/lib/component-props";
import { cn } from "@/lib/utils";
import { measureSvgDisplaySize } from "./drawing-scale";
import { normalizeMermaidSource } from "./normalize-mermaid-source";
import { getMermaidErrorMessage, renderMermaidSvg } from "./render-mermaid";
import { createViewport, fitViewport, MAX_SCALE, MIN_SCALE, panViewport, pointerDistance, pointerMidpoint, type ViewportTransform, viewportToCssTransform, ZOOM_STEP, zoomViewportAtPoint, zoomViewportBy } from "./viewport-transform";
export interface MermaidDiagramProps extends ComponentPropsWithoutChildren<"figure"> {
  source: string;
  title?: string;
  caption?: string;
  alt?: string;
}
type DiagramStatus = {
  type: "loading";
} | {
  type: "ready";
  svg: string;
} | {
  type: "error";
  message: string;
};
type PointerPosition = {
  x: number;
  y: number;
};
const measureSvg = (svg: SVGSVGElement): {
  width: number;
  height: number;
} => {
  const display = measureSvgDisplaySize(svg);
  if (display) {
    return display;
  }
  try {
    const bbox = svg.getBBox();
    if (bbox.width > 0 && bbox.height > 0) {
      return {
        width: bbox.width,
        height: bbox.height
      };
    }
  } catch {
    // getBBox throws when the SVG is not rendered.
  }
  return {
    width: svg.clientWidth,
    height: svg.clientHeight
  };
};
const getContainerOrigin = (container: HTMLElement | null): {
  x: number;
  y: number;
} => {
  if (!container) {
    return {
      x: 0,
      y: 0
    };
  }
  const rect = container.getBoundingClientRect();
  return {
    x: rect.width / 2,
    y: rect.height / 2
  };
};
const getPointInContainer = (container: HTMLElement, clientX: number, clientY: number): PointerPosition => {
  const rect = container.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top
  };
};
type DiagramControlsProps = {
  scale: number;
  viewportId: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
};
const DiagramControls: FC<DiagramControlsProps> = t0 => {
  const $ = _c(23);
  const {
    scale,
    viewportId,
    onZoomIn,
    onZoomOut,
    onFit
  } = t0;
  let t1;
  if ($[0] !== scale) {
    t1 = Math.round(scale * 100);
    $[0] = scale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const zoomPercent = t1;
  const zoomInDisabled = scale >= MAX_SCALE;
  const zoomOutDisabled = scale <= MIN_SCALE;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <LuPlus aria-hidden={true} className="size-4" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onZoomIn || $[4] !== viewportId || $[5] !== zoomInDisabled) {
    t3 = <Button type="button" size="small" variant="highlight" className="size-9 p-0" aria-label="Zoom in" aria-controls={viewportId} disabled={zoomInDisabled} onClick={onZoomIn}>{t2}</Button>;
    $[3] = onZoomIn;
    $[4] = viewportId;
    $[5] = zoomInDisabled;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <LuMinus aria-hidden={true} className="size-4" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onZoomOut || $[9] !== viewportId || $[10] !== zoomOutDisabled) {
    t5 = <Button type="button" size="small" variant="highlight" className="size-9 p-0" aria-label="Zoom out" aria-controls={viewportId} disabled={zoomOutDisabled} onClick={onZoomOut}>{t4}</Button>;
    $[8] = onZoomOut;
    $[9] = viewportId;
    $[10] = zoomOutDisabled;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LuScan aria-hidden={true} className="size-4" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onFit || $[14] !== viewportId) {
    t7 = <Button type="button" size="small" variant="secondary" className="size-9 p-0" aria-label="Fit diagram to view" aria-controls={viewportId} onClick={onFit}>{t6}</Button>;
    $[13] = onFit;
    $[14] = viewportId;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== zoomPercent) {
    t8 = <p aria-live="polite" className="rounded-md border-2 border-black bg-white px-1 py-0.5 text-center font-mono text-[10px] font-bold">{zoomPercent}%</p>;
    $[16] = zoomPercent;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t3 || $[19] !== t5 || $[20] !== t7 || $[21] !== t8) {
    t9 = <div className="absolute right-3 bottom-3 z-10 flex flex-col items-stretch gap-1.5">{t3}{t5}{t7}{t8}</div>;
    $[18] = t3;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
export const MermaidDiagram: FC<MermaidDiagramProps> = t0 => {
  const $ = _c(98);
  let alt;
  let caption;
  let className;
  let props;
  let source;
  let title;
  if ($[0] !== t0) {
    ({
      source,
      title,
      caption,
      alt,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = caption;
    $[3] = className;
    $[4] = props;
    $[5] = source;
    $[6] = title;
  } else {
    alt = $[1];
    caption = $[2];
    className = $[3];
    props = $[4];
    source = $[5];
    title = $[6];
  }
  const reactId = useId();
  let t1;
  if ($[7] !== reactId) {
    let t2;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /[^a-zA-Z0-9_-]/g;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = reactId.replace(t2, "");
    $[7] = reactId;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const safeId = t1;
  const viewportId = `mermaid-viewport-${safeId}`;
  const instructionsId = `mermaid-instructions-${safeId}`;
  const captionId = caption ? `mermaid-caption-${safeId}` : undefined;
  let t2;
  if ($[10] !== source) {
    t2 = normalizeMermaidSource(source);
    $[10] = source;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const normalizedSource = t2;
  const accessibleName = alt || title || "Mermaid diagram";
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const pointersRef = useRef(t3);
  const lastPointerRef = useRef(null);
  const pinchDistanceRef = useRef(null);
  const userAdjustedRef = useRef(false);
  const renderCountRef = useRef(0);
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      type: "loading"
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const [status, setStatus] = useState(t4);
  const [transform, setTransform] = useState(createViewport);
  const [isPanning, setIsPanning] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(null);
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const container = viewportRef.current;
      const svg = contentRef.current?.querySelector("svg");
      if (!container || !svg) {
        setTransform(createViewport());
        setViewportHeight(null);
        return;
      }
      const content = measureSvg(svg);
      const containerWidth = container.getBoundingClientRect().width;
      const availableWidth = Math.max(containerWidth - 48, 1);
      const widthRatio = availableWidth / content.width;
      const naturalHeight = content.height * Math.min(widthRatio, 1) + 48;
      const remPx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const maxH = Math.min(window.innerHeight * 0.7, remPx * 40);
      const containerHeight = Math.min(Math.max(naturalHeight, remPx * 8), maxH);
      setViewportHeight(containerHeight);
      setTransform(fitViewport({
        contentWidth: content.width,
        contentHeight: content.height,
        containerWidth,
        containerHeight
      }));
      userAdjustedRef.current = false;
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const fitToView = useEffectEvent(t5);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (factor, origin) => {
      const zoomOrigin = origin ?? getContainerOrigin(viewportRef.current);
      userAdjustedRef.current = true;
      setTransform(viewport => zoomViewportBy(viewport, factor, zoomOrigin));
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const zoomBy = useEffectEvent(t6);
  let t7;
  let t8;
  if ($[16] !== normalizedSource || $[17] !== safeId) {
    t7 = () => {
      if (!normalizedSource) {
        return;
      }
      let cancelled = false;
      renderCountRef.current = renderCountRef.current + 1;
      const renderId = `mermaid-${safeId}-${renderCountRef.current}`;
      setStatus({
        type: "loading"
      });
      setTransform(createViewport());
      setViewportHeight(null);
      userAdjustedRef.current = false;
      const render = async () => {
        ;
        try {
          const svg_0 = await renderMermaidSvg(normalizedSource, renderId);
          if (!cancelled) {
            setStatus({
              type: "ready",
              svg: svg_0
            });
          }
        } catch (t9) {
          const error = t9;
          if (!cancelled) {
            setStatus({
              type: "error",
              message: getMermaidErrorMessage(error)
            });
          }
        }
      };
      render();
      return () => {
        cancelled = true;
      };
    };
    t8 = [normalizedSource, safeId];
    $[16] = normalizedSource;
    $[17] = safeId;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  const svgMarkup = status.type === "ready" ? status.svg : undefined;
  let t9;
  if ($[20] !== fitToView || $[21] !== svgMarkup) {
    t9 = () => {
      if (!svgMarkup) {
        return;
      }
      fitToView();
    };
    $[20] = fitToView;
    $[21] = svgMarkup;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== svgMarkup) {
    t10 = [svgMarkup];
    $[23] = svgMarkup;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[25] !== fitToView || $[26] !== svgMarkup) {
    t11 = () => {
      const container_0 = viewportRef.current;
      if (!container_0 || !svgMarkup) {
        return;
      }
      const observer = new ResizeObserver(() => {
        if (!userAdjustedRef.current) {
          fitToView();
        }
      });
      observer.observe(container_0);
      return () => observer.disconnect();
    };
    $[25] = fitToView;
    $[26] = svgMarkup;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== svgMarkup) {
    t12 = [svgMarkup];
    $[28] = svgMarkup;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  if ($[30] !== svgMarkup || $[31] !== zoomBy) {
    t13 = () => {
      const container_1 = viewportRef.current;
      if (!container_1 || !svgMarkup) {
        return;
      }
      const handleWheel = event => {
        event.preventDefault();
        const origin_0 = getPointInContainer(container_1, event.clientX, event.clientY);
        const factor_0 = 2 ** (-event.deltaY / 300);
        zoomBy(factor_0, origin_0);
      };
      container_1.addEventListener("wheel", handleWheel, {
        passive: false
      });
      return () => {
        container_1.removeEventListener("wheel", handleWheel);
      };
    };
    $[30] = svgMarkup;
    $[31] = zoomBy;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== svgMarkup) {
    t14 = [svgMarkup];
    $[33] = svgMarkup;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  useEffect(t13, t14);
  if (!normalizedSource) {
    return null;
  }
  let t15;
  if ($[35] !== status.type) {
    t15 = event_0 => {
      if (status.type !== "ready") {
        return;
      }
      const container_2 = event_0.currentTarget;
      const point = getPointInContainer(container_2, event_0.clientX, event_0.clientY);
      pointersRef.current.set(event_0.pointerId, point);
      lastPointerRef.current = {
        x: event_0.clientX,
        y: event_0.clientY
      };
      container_2.setPointerCapture(event_0.pointerId);
      if (pointersRef.current.size === 1) {
        setIsPanning(true);
      }
    };
    $[35] = status.type;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const handlePointerDown = t15;
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = event_1 => {
      const container_3 = event_1.currentTarget;
      const pointers = pointersRef.current;
      if (!pointers.has(event_1.pointerId)) {
        return;
      }
      const point_0 = getPointInContainer(container_3, event_1.clientX, event_1.clientY);
      pointers.set(event_1.pointerId, point_0);
      if (pointers.size === 2) {
        const [first, second] = [...pointers.values()];
        if (!first || !second) {
          return;
        }
        const nextDistance = pointerDistance(first, second);
        const previousDistance = pinchDistanceRef.current;
        pinchDistanceRef.current = nextDistance;
        if (previousDistance && previousDistance > 0) {
          userAdjustedRef.current = true;
          setTransform(viewport_0 => zoomViewportAtPoint(viewport_0, pointerMidpoint(first, second), viewport_0.scale * (nextDistance / previousDistance)));
        }
        setIsPanning(false);
        return;
      }
      const last = lastPointerRef.current;
      if (!last || pointers.size !== 1) {
        return;
      }
      const dx = event_1.clientX - last.x;
      const dy = event_1.clientY - last.y;
      lastPointerRef.current = {
        x: event_1.clientX,
        y: event_1.clientY
      };
      userAdjustedRef.current = true;
      setTransform(viewport_1 => panViewport(viewport_1, dx, dy));
    };
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const handlePointerMove = t16;
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    const endPointer = pointerId => {
      pointersRef.current.delete(pointerId);
      if (pointersRef.current.size < 2) {
        pinchDistanceRef.current = null;
      }
      if (pointersRef.current.size === 0) {
        lastPointerRef.current = null;
        setIsPanning(false);
      }
    };
    t17 = event_2 => {
      endPointer(event_2.pointerId);
    };
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const handlePointerUp = t17;
  let viewportContent;
  bb0: switch (status.type) {
    case "loading":
      {
        let t18;
        if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
          t18 = <p className="m-0 px-4 py-8 text-center font-mono text-sm font-semibold text-zinc-600">Rendering diagram…</p>;
          $[39] = t18;
        } else {
          t18 = $[39];
        }
        viewportContent = t18;
        break bb0;
      }
    case "error":
      {
        let t18;
        if ($[40] !== status.message) {
          t18 = <p className="m-0 font-mono text-sm font-bold text-red-700">{status.message}</p>;
          $[40] = status.message;
          $[41] = t18;
        } else {
          t18 = $[41];
        }
        let t19;
        if ($[42] !== normalizedSource) {
          t19 = <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border-2 border-black bg-white p-3 font-mono text-xs">{normalizedSource}</pre>;
          $[42] = normalizedSource;
          $[43] = t19;
        } else {
          t19 = $[43];
        }
        let t20;
        if ($[44] !== t18 || $[45] !== t19) {
          t20 = <div className="space-y-3 p-4">{t18}{t19}</div>;
          $[44] = t18;
          $[45] = t19;
          $[46] = t20;
        } else {
          t20 = $[46];
        }
        viewportContent = t20;
        break bb0;
      }
    case "ready":
      {
        let t18;
        if ($[47] !== transform) {
          t18 = viewportToCssTransform(transform);
          $[47] = transform;
          $[48] = t18;
        } else {
          t18 = $[48];
        }
        let t19;
        if ($[49] !== t18) {
          t19 = {
            transform: t18
          };
          $[49] = t18;
          $[50] = t19;
        } else {
          t19 = $[50];
        }
        let t20;
        if ($[51] !== status.svg) {
          t20 = parse(status.svg);
          $[51] = status.svg;
          $[52] = t20;
        } else {
          t20 = $[52];
        }
        let t21;
        if ($[53] !== t19 || $[54] !== t20) {
          t21 = <div ref={contentRef} className="inline-block origin-top-left will-change-transform [&_svg]:h-auto [&_svg]:max-w-none" style={t19}>{t20}</div>;
          $[53] = t19;
          $[54] = t20;
          $[55] = t21;
        } else {
          t21 = $[55];
        }
        viewportContent = t21;
        break bb0;
      }
    default:
      {
        const exhaustive = status;
        viewportContent = exhaustive;
      }
  }
  let t18;
  if ($[56] !== className) {
    t18 = cn("overflow-hidden rounded-md border-2 border-black bg-[var(--bg-primary)] shadow-[4px_4px_0_0]", className);
    $[56] = className;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== title) {
    t19 = title ? <div className="border-b-2 border-black bg-yellow-300 px-4 py-2 font-mono text-sm font-semibold">{title}</div> : null;
    $[58] = title;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== instructionsId) {
    t20 = <p id={instructionsId} className="sr-only">Scroll or pinch to zoom. Drag to pan. Use the zoom in, zoom out, and fit buttons to adjust the view from the keyboard.</p>;
    $[60] = instructionsId;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== normalizedSource) {
    t21 = <pre className="sr-only">{normalizedSource}</pre>;
    $[62] = normalizedSource;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  const t22 = status.type === "loading";
  const t23 = captionId ? `${instructionsId} ${captionId}` : instructionsId;
  const t24 = viewportHeight == null && "min-h-[12rem]";
  const t25 = isPanning ? "cursor-grabbing" : "cursor-grab";
  let t26;
  if ($[64] !== t24 || $[65] !== t25) {
    t26 = cn("relative max-h-[min(70svh,40rem)] overflow-hidden touch-none", t24, t25);
    $[64] = t24;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== viewportHeight) {
    t27 = viewportHeight != null ? {
      height: viewportHeight
    } : undefined;
    $[67] = viewportHeight;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== handlePointerDown || $[70] !== t22 || $[71] !== t23 || $[72] !== t26 || $[73] !== t27 || $[74] !== viewportContent || $[75] !== viewportId) {
    t28 = <div ref={viewportRef} id={viewportId} aria-busy={t22} aria-describedby={t23} className={t26} style={t27} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}>{viewportContent}</div>;
    $[69] = handlePointerDown;
    $[70] = t22;
    $[71] = t23;
    $[72] = t26;
    $[73] = t27;
    $[74] = viewportContent;
    $[75] = viewportId;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== fitToView || $[78] !== status.type || $[79] !== transform || $[80] !== viewportId || $[81] !== zoomBy) {
    t29 = status.type === "ready" ? <DiagramControls scale={transform.scale} viewportId={viewportId} onZoomIn={() => zoomBy(ZOOM_STEP)} onZoomOut={() => zoomBy(1 / ZOOM_STEP)} onFit={fitToView} /> : null;
    $[77] = fitToView;
    $[78] = status.type;
    $[79] = transform;
    $[80] = viewportId;
    $[81] = zoomBy;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== t28 || $[84] !== t29) {
    t30 = <div className="relative">{t28}{t29}</div>;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  let t31;
  if ($[86] !== caption || $[87] !== captionId) {
    t31 = caption ? <figcaption id={captionId} className="border-t-2 border-black bg-white px-4 py-2 text-sm font-semibold">{caption}</figcaption> : null;
    $[86] = caption;
    $[87] = captionId;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== accessibleName || $[90] !== props || $[91] !== t18 || $[92] !== t19 || $[93] !== t20 || $[94] !== t21 || $[95] !== t30 || $[96] !== t31) {
    t32 = <figure className={t18} aria-label={accessibleName} {...props}>{t19}{t20}{t21}{t30}{t31}</figure>;
    $[89] = accessibleName;
    $[90] = props;
    $[91] = t18;
    $[92] = t19;
    $[93] = t20;
    $[94] = t21;
    $[95] = t30;
    $[96] = t31;
    $[97] = t32;
  } else {
    t32 = $[97];
  }
  return t32;
};